import React, { useState } from "react";
import axios from "axios";
import { siteData } from "../../data/mock";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../ui/accordion";
import {
  Dumbbell,
  ShieldCheck,
  Sparkles,
  Check,
  CircleDot,
  Send,
  Quote,
  Play,
  Phone,
  Mail,
  User,
  MessageSquare,
  CheckCircle2,
  Instagram,
  Users,
  ArrowUpRight,
} from "lucide-react";
import { toast } from "sonner";

const EffortSection = () => {
  const { effort } = siteData;
  return (
    <section className="py-28 lg:py-36 relative">
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-24 h-px bg-gradient-to-r from-transparent via-[#7dfabf]/30 to-transparent" />
      <div className="max-w-4xl mx-auto px-6 text-center">
        <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-[#7dfabf]/20 bg-[#7dfabf]/5 mb-6">
          <Dumbbell size={16} className="text-[#7dfabf]" />
          <span className="text-sm text-[#7dfabf] font-medium tracking-wider uppercase font-body">What It Takes</span>
        </div>
        <h2 className="text-4xl lg:text-5xl font-black text-white mb-6 tracking-tight font-heading">{effort.title}</h2>
        <p className="text-lg text-white/60 mb-10 font-body">{effort.intro}</p>
        <div className="grid sm:grid-cols-2 gap-4 max-w-2xl mx-auto mb-12">
          {effort.requirements.map((req, i) => (
            <div key={i} className="flex items-center gap-3 p-5 rounded-xl bg-[#0A0A0A] border border-white/5 hover:border-[#7dfabf]/15 transition-colors duration-300">
              <CircleDot size={18} className="text-[#7dfabf] flex-shrink-0" />
              <span className="text-white/80 text-left font-body">{req}</span>
            </div>
          ))}
        </div>
        <div className="p-6 rounded-2xl bg-[#7dfabf]/5 border border-[#7dfabf]/20">
          <p className="text-xl text-[#7dfabf] font-semibold font-body">{effort.highlight}</p>
        </div>
      </div>
    </section>
  );
};

const TestimonialsSection = () => {
  const { testimonials } = siteData;
  const [videoPlaying, setVideoPlaying] = useState(false);

  return (
    <section id="testimonials" className="py-28 lg:py-36 relative">
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-24 h-px bg-gradient-to-r from-transparent via-[#7dfabf]/30 to-transparent" />
      <div className="max-w-6xl mx-auto px-6">
        <h2 className="text-4xl lg:text-5xl font-black text-white text-center mb-16 tracking-tight font-heading">
          {testimonials.title}
        </h2>

        {/* Video Testimonial */}
        <div className="mb-16 max-w-3xl mx-auto">
          <div className="relative rounded-2xl overflow-hidden bg-[#0A0A0A] border border-white/5">
            <video
              src={testimonials.video.url}
              controls
              playsInline
              preload="metadata"
              poster=""
              className="w-full aspect-video object-cover"
              onPlay={() => setVideoPlaying(true)}
              onPause={() => setVideoPlaying(false)}
            />
          </div>
          <div className="mt-6 p-6 rounded-2xl bg-[#0A0A0A] border border-white/5">
            <Quote size={24} className="text-[#7dfabf]/40 mb-3" />
            <p className="text-white/80 text-lg italic leading-relaxed mb-4 font-body">
              "{testimonials.video.quote}"
            </p>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-[#7dfabf]/10 flex items-center justify-center">
                <User size={18} className="text-[#7dfabf]" />
              </div>
              <div>
                <p className="text-white font-semibold font-heading">{testimonials.video.name}</p>
                <p className="text-[#7dfabf] text-sm font-body">{testimonials.video.duration}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Text Testimonials */}
        <div className="grid sm:grid-cols-2 gap-6">
          {testimonials.items.map((item, i) => (
            <div
              key={i}
              className="p-6 sm:p-8 rounded-2xl bg-[#0A0A0A] border border-white/5 hover:border-[#7dfabf]/15 transition-all duration-500 flex flex-col"
            >
              <Quote size={24} className="text-[#7dfabf]/30 mb-4 flex-shrink-0" />
              <p className="text-white/80 leading-relaxed mb-6 font-body italic text-sm sm:text-base flex-grow">
                "{item.quote}"
              </p>
              <div className="flex items-center justify-between mt-auto gap-3">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-[#7dfabf]/10 flex items-center justify-center flex-shrink-0">
                    <span className="text-[#7dfabf] font-bold font-heading">{item.name[0]}</span>
                  </div>
                  <p className="text-white font-semibold font-heading">{item.name}</p>
                </div>
                <span className="px-3 py-1 rounded-full bg-[#7dfabf]/10 text-[#7dfabf] text-xs font-semibold font-body whitespace-nowrap">
                  {item.result}
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

const FAQSection = () => {
  const { faq } = siteData;
  return (
    <section id="faq" className="py-28 lg:py-36 relative">
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-24 h-px bg-gradient-to-r from-transparent via-[#7dfabf]/30 to-transparent" />
      <div className="max-w-3xl mx-auto px-6">
        <h2 className="text-4xl lg:text-5xl font-black text-white text-center mb-16 tracking-tight font-heading">{faq.title}</h2>
        <Accordion type="single" collapsible className="space-y-3">
          {faq.items.map((item, i) => (
            <AccordionItem
              key={i}
              value={`faq-${i}`}
              className="border-0 rounded-xl bg-[#0A0A0A] px-6 data-[state=open]:bg-[#111111] transition-colors duration-300"
            >
              <AccordionTrigger className="text-white text-left font-semibold text-base hover:no-underline hover:text-[#7dfabf] py-5 [&>svg]:text-[#7dfabf] font-heading">
                {item.question}
              </AccordionTrigger>
              <AccordionContent className="text-white/60 leading-relaxed pb-5 font-body">
                {item.answer}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </div>
    </section>
  );
};

const InvestmentSection = () => {
  const { investment } = siteData;
  return (
    <section id="investment" className="py-28 lg:py-36 relative">
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-24 h-px bg-gradient-to-r from-transparent via-[#7dfabf]/30 to-transparent" />
      <div className="max-w-4xl mx-auto px-6">
        <h2 className="text-4xl lg:text-5xl font-black text-white text-center mb-16 tracking-tight font-heading">{investment.title}</h2>
        <div className="grid md:grid-cols-2 gap-8">
          <div className="relative p-8 lg:p-10 rounded-2xl bg-[#0A0A0A] border-2 border-[#7dfabf]/30 hover:border-[#7dfabf]/50 transition-all duration-500">
            <div className="absolute -top-3 left-8 px-4 py-1 bg-[#7dfabf] rounded-full">
              <span className="text-xs font-bold text-black uppercase tracking-wider font-body">Best Value</span>
            </div>
            <p className="text-sm text-white/60 mb-2 mt-2 font-body">One-time payment</p>
            <p className="text-5xl font-black text-white mb-8 font-heading">{investment.fullPrice}</p>
            <ul className="space-y-3 mb-8">
              {investment.includes.map((item, i) => (
                <li key={i} className="flex items-center gap-3">
                  <Check size={16} className="text-[#7dfabf]" />
                  <span className="text-white/80 font-body">{item}</span>
                </li>
              ))}
            </ul>
            <button
              onClick={() => document.querySelector("#apply")?.scrollIntoView({ behavior: "smooth" })}
              className="w-full py-4 bg-[#7dfabf] text-black font-bold rounded-lg hover:bg-[#6ae0a8] transition-all duration-300 hover:shadow-lg hover:shadow-[#7dfabf]/20 font-body"
            >
              Apply Now
            </button>
          </div>
          <div className="p-8 lg:p-10 rounded-2xl bg-[#0A0A0A] border border-white/5 hover:border-[#7dfabf]/20 transition-all duration-500">
            <p className="text-sm text-white/60 mb-2 font-body">{investment.splitNote}</p>
            <p className="text-5xl font-black text-white mb-8 font-heading">{investment.splitPrice}</p>
            <ul className="space-y-3 mb-8">
              {investment.includes.map((item, i) => (
                <li key={i} className="flex items-center gap-3">
                  <Check size={16} className="text-[#7dfabf]/60" />
                  <span className="text-white/80 font-body">{item}</span>
                </li>
              ))}
            </ul>
            <button
              onClick={() => document.querySelector("#apply")?.scrollIntoView({ behavior: "smooth" })}
              className="w-full py-4 border border-[#7dfabf] text-[#7dfabf] font-bold rounded-lg hover:bg-[#7dfabf]/10 transition-all duration-300 font-body"
            >
              Apply Now
            </button>
          </div>
        </div>
        <p className="text-center text-white/60 mt-8 font-medium font-body">{investment.noUpsells}</p>
      </div>
    </section>
  );
};

const GuaranteeSection = () => {
  const { guarantee } = siteData;
  return (
    <section className="py-28 lg:py-36 relative">
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-24 h-px bg-gradient-to-r from-transparent via-[#7dfabf]/30 to-transparent" />
      <div className="max-w-3xl mx-auto px-6 text-center">
        <div className="p-10 lg:p-14 rounded-2xl bg-[#0A0A0A] border border-white/5">
          <div className="w-16 h-16 rounded-2xl bg-[#7dfabf]/10 flex items-center justify-center mx-auto mb-8">
            <ShieldCheck size={32} className="text-[#7dfabf]" />
          </div>
          <h2 className="text-3xl lg:text-4xl font-black text-white mb-6 tracking-tight font-heading">{guarantee.title}</h2>
          <p className="text-lg text-white/60 mb-8 font-body">{guarantee.description}</p>
          <ul className="space-y-4 mb-8">
            {guarantee.points.map((point, i) => (
              <li key={i} className="flex items-center justify-center gap-3">
                <Check size={18} className="text-[#7dfabf]" />
                <span className="text-white text-lg font-medium font-body">{point}</span>
              </li>
            ))}
          </ul>
          <p className="text-xl text-[#7dfabf] font-semibold font-body">{guarantee.bottomLine}</p>
        </div>
      </div>
    </section>
  );
};

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Validation helpers
const validateEmail = (email) => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (!emailRegex.test(email)) return false;
  // Check for common typos
  const commonTypos = ['gmial.com', 'gmal.com', 'gmail.con', 'gamil.com', 'hotmal.com', 'yahooo.com'];
  const domain = email.split('@')[1]?.toLowerCase();
  if (commonTypos.includes(domain)) return false;
  return true;
};

const formatUKPhone = (value) => {
  // Remove all non-digits
  const digits = value.replace(/\D/g, '');
  
  // Format as UK mobile: 07XXX XXX XXX or +44 7XXX XXX XXX
  if (digits.startsWith('44') && digits.length > 2) {
    const rest = digits.slice(2);
    if (rest.length <= 4) return `+44 ${rest}`;
    if (rest.length <= 7) return `+44 ${rest.slice(0, 4)} ${rest.slice(4)}`;
    return `+44 ${rest.slice(0, 4)} ${rest.slice(4, 7)} ${rest.slice(7, 10)}`;
  }
  
  if (digits.startsWith('0')) {
    if (digits.length <= 5) return digits;
    if (digits.length <= 8) return `${digits.slice(0, 5)} ${digits.slice(5)}`;
    return `${digits.slice(0, 5)} ${digits.slice(5, 8)} ${digits.slice(8, 11)}`;
  }
  
  return value;
};

const ApplySection = () => {
  const { apply, confirmation } = siteData;
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    whyJoin: "",
    prepared: "",
  });
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState({});

  const handlePhoneChange = (e) => {
    const formatted = formatUKPhone(e.target.value);
    setFormData({ ...formData, phone: formatted });
    if (errors.phone) setErrors({ ...errors, phone: null });
  };

  const handleEmailChange = (e) => {
    setFormData({ ...formData, email: e.target.value });
    if (errors.email) setErrors({ ...errors, email: null });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const newErrors = {};
    
    if (!formData.name.trim()) {
      newErrors.name = "Please enter your name";
    }
    
    if (!formData.email.trim()) {
      newErrors.email = "Please enter your email";
    } else if (!validateEmail(formData.email)) {
      newErrors.email = "Please check your email address";
    }
    
    if (!formData.phone.trim()) {
      newErrors.phone = "Please enter your phone number";
    } else if (formData.phone.replace(/\D/g, '').length < 10) {
      newErrors.phone = "Please enter a valid phone number";
    }
    
    if (!formData.whyJoin.trim()) {
      newErrors.whyJoin = "Please tell us why you want to join";
    }
    
    if (!formData.prepared.trim()) {
      newErrors.prepared = "Please answer this question";
    }
    
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      toast.error("Please fix the errors in the form");
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(`${API}/applications`, {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        why_join: formData.whyJoin,
        prepared: formData.prepared,
      });
      setSubmitted(true);
      toast.success("Application submitted successfully!");
      
      // Track Meta Pixel Lead event
      if (typeof window !== 'undefined' && window.fbq) {
        window.fbq('track', 'Lead', {
          content_name: 'Booze to Beast Application',
          content_category: 'Application Form',
        });
      }
    } catch (err) {
      console.error("Application submission failed:", err);
      toast.error("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const firstName = formData.name.split(" ")[0] || "";

  return (
    <section id="apply" className="py-28 lg:py-36 relative">
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-24 h-px bg-gradient-to-r from-transparent via-[#7dfabf]/30 to-transparent" />
      <div className="max-w-3xl mx-auto px-6">
        {submitted ? (
          /* Confirmation Page */
          <div className="text-center">
            <div className="p-10 lg:p-14 rounded-2xl bg-[#0A0A0A] border border-[#7dfabf]/20">
              <div className="w-20 h-20 rounded-full bg-[#7dfabf]/10 flex items-center justify-center mx-auto mb-8">
                <CheckCircle2 size={40} className="text-[#7dfabf]" />
              </div>
              <h2 className="text-3xl lg:text-4xl font-black text-white mb-6 tracking-tight font-heading">
                Thanks for applying{firstName ? `, ${firstName}` : ""}.
              </h2>
              <p className="text-lg text-white/70 leading-relaxed mb-8 font-body">
                {confirmation.message}
              </p>
              <div className="p-6 rounded-xl bg-[#7dfabf]/5 border border-[#7dfabf]/20">
                <p className="text-[#7dfabf] font-semibold text-lg font-body">
                  Deposit: {confirmation.depositAmount}
                </p>
              </div>
            </div>
          </div>
        ) : (
          /* Application Form */
          <>
            <div className="text-center mb-12">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-[#7dfabf]/20 bg-[#7dfabf]/5 mb-6">
                <Sparkles size={16} className="text-[#7dfabf]" />
                <span className="text-sm text-[#7dfabf] font-medium tracking-wider uppercase font-body">Take The Next Step</span>
              </div>
              <h2 className="text-4xl lg:text-5xl font-black text-white mb-4 tracking-tight font-heading">{apply.title}</h2>
              <p className="text-lg text-white/60 font-body">{apply.subtitle}</p>
              <p className="text-white/60 mt-2 font-body">{apply.description}</p>
            </div>

            <form onSubmit={handleSubmit} className="p-8 lg:p-10 rounded-2xl bg-[#0A0A0A] border border-white/5 space-y-6">
              <div>
                <label className="flex items-center gap-2 text-sm font-medium text-white/60 mb-2 font-body">
                  <User size={14} /> Your Name
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => {
                    setFormData({ ...formData, name: e.target.value });
                    if (errors.name) setErrors({ ...errors, name: null });
                  }}
                  placeholder="Enter your full name"
                  className={`w-full px-5 py-4 rounded-xl bg-black border ${errors.name ? 'border-red-500' : 'border-white/10'} text-white placeholder:text-white/20 focus:outline-none focus:border-[#7dfabf]/50 focus:ring-1 focus:ring-[#7dfabf]/20 transition-all duration-300 font-body`}
                />
                {errors.name && <p className="text-red-400 text-sm mt-1 font-body">{errors.name}</p>}
              </div>
              <div className="grid sm:grid-cols-2 gap-6">
                <div>
                  <label className="flex items-center gap-2 text-sm font-medium text-white/60 mb-2 font-body">
                    <Mail size={14} /> Email Address
                  </label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={handleEmailChange}
                    placeholder="your@email.com"
                    className={`w-full px-5 py-4 rounded-xl bg-black border ${errors.email ? 'border-red-500' : 'border-white/10'} text-white placeholder:text-white/20 focus:outline-none focus:border-[#7dfabf]/50 focus:ring-1 focus:ring-[#7dfabf]/20 transition-all duration-300 font-body`}
                  />
                  {errors.email && <p className="text-red-400 text-sm mt-1 font-body">{errors.email}</p>}
                </div>
                <div>
                  <label className="flex items-center gap-2 text-sm font-medium text-white/60 mb-2 font-body">
                    <Phone size={14} /> Phone Number
                  </label>
                  <input
                    type="tel"
                    value={formData.phone}
                    onChange={handlePhoneChange}
                    placeholder="+44 7XXX XXX XXX"
                    className={`w-full px-5 py-4 rounded-xl bg-black border ${errors.phone ? 'border-red-500' : 'border-white/10'} text-white placeholder:text-white/20 focus:outline-none focus:border-[#7dfabf]/50 focus:ring-1 focus:ring-[#7dfabf]/20 transition-all duration-300 font-body`}
                  />
                  {errors.phone && <p className="text-red-400 text-sm mt-1 font-body">{errors.phone}</p>}
                </div>
              </div>
              <div>
                <label className="flex items-center gap-2 text-sm font-medium text-white/60 mb-2 font-body">
                  <MessageSquare size={14} /> Why do you want to join?
                </label>
                <textarea
                  value={formData.whyJoin}
                  onChange={(e) => {
                    setFormData({ ...formData, whyJoin: e.target.value });
                    if (errors.whyJoin) setErrors({ ...errors, whyJoin: null });
                  }}
                  placeholder="Tell us about where you're at and why you're ready..."
                  rows={4}
                  className={`w-full px-5 py-4 rounded-xl bg-black border ${errors.whyJoin ? 'border-red-500' : 'border-white/10'} text-white placeholder:text-white/20 focus:outline-none focus:border-[#7dfabf]/50 focus:ring-1 focus:ring-[#7dfabf]/20 transition-all duration-300 resize-none font-body`}
                />
                {errors.whyJoin && <p className="text-red-400 text-sm mt-1 font-body">{errors.whyJoin}</p>}
              </div>
              <div>
                <label className="flex items-center gap-2 text-sm font-medium text-white/60 mb-2 font-body">
                  <CheckCircle2 size={14} /> Are you prepared to do the work in order to get the results?
                </label>
                <textarea
                  value={formData.prepared}
                  onChange={(e) => {
                    setFormData({ ...formData, prepared: e.target.value });
                    if (errors.prepared) setErrors({ ...errors, prepared: null });
                  }}
                  placeholder="Be honest..."
                  rows={3}
                  className={`w-full px-5 py-4 rounded-xl bg-black border ${errors.prepared ? 'border-red-500' : 'border-white/10'} text-white placeholder:text-white/20 focus:outline-none focus:border-[#7dfabf]/50 focus:ring-1 focus:ring-[#7dfabf]/20 transition-all duration-300 resize-none font-body`}
                />
                {errors.prepared && <p className="text-red-400 text-sm mt-1 font-body">{errors.prepared}</p>}
              </div>
              <button
                type="submit"
                disabled={submitting}
                className="w-full py-4 bg-[#7dfabf] text-black font-bold text-lg rounded-lg hover:bg-[#6ae0a8] transition-all duration-300 hover:shadow-xl hover:shadow-[#7dfabf]/20 flex items-center justify-center gap-2 font-body disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {submitting ? (
                  <>
                    <div className="w-5 h-5 border-2 border-black/30 border-t-black rounded-full animate-spin" />
                    Submitting...
                  </>
                ) : (
                  <>
                    <Send size={20} />
                    {apply.cta}
                  </>
                )}
              </button>
              <p className="text-center text-sm text-white/30 font-body">{apply.bottomLine}</p>
            </form>
          </>
        )}
      </div>
    </section>
  );
};

const Footer = () => {
  return (
    <footer className="py-12 border-t border-white/5">
      <div className="max-w-6xl mx-auto px-6">
        {/* Disclaimer */}
        <div className="mb-10 p-6 rounded-xl bg-[#0A0A0A] border border-white/5">
          <p className="text-xs text-white/40 leading-relaxed font-body text-center">
            Booze to Beast is a coaching and personal development programme. It is not a medical or therapeutic service and does not constitute medical advice, diagnosis, or treatment. Results vary between individuals. If you are concerned about your alcohol use or mental health, please consult a qualified healthcare professional.
          </p>
        </div>

        <div className="flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="flex items-center gap-3">
            <img
              src={siteData.brand.logoIcon}
              alt="Booze to Beast"
              className="w-8 h-8 rounded-lg"
            />
            <span className="text-lg font-bold text-white font-heading">
              {siteData.brand.name}
            </span>
          </div>
          <p className="text-sm text-white/30 font-body">
            &copy; {new Date().getFullYear()} Booze to Beast. All rights reserved.
          </p>
          <div className="flex items-center gap-6">
            <a 
              href="https://www.instagram.com/marcusjonescoaching/" 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-white/30 hover:text-[#7dfabf] transition-colors duration-300"
              aria-label="Instagram"
            >
              <Instagram size={20} />
            </a>
            <a href="#" className="text-sm text-white/30 hover:text-[#7dfabf] transition-colors duration-300 font-body">Privacy Policy</a>
            <a href="#" className="text-sm text-white/30 hover:text-[#7dfabf] transition-colors duration-300 font-body">Terms</a>
          </div>
        </div>
      </div>
    </footer>
  );
};

const ConnectSection = () => {
  return (
    <section className="py-20 lg:py-28 relative">
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-24 h-px bg-gradient-to-r from-transparent via-[#7dfabf]/30 to-transparent" />
      <div className="max-w-4xl mx-auto px-6 text-center">
        <h2 className="text-3xl lg:text-4xl font-black text-white mb-4 tracking-tight font-heading">
          Connect With Marcus
        </h2>
        <p className="text-lg text-white/60 mb-12 font-body">
          Follow the journey and join the community
        </p>
        
        <div className="grid sm:grid-cols-2 gap-6 max-w-2xl mx-auto">
          {/* Instagram */}
          <a
            href="https://www.instagram.com/marcusjonescoaching/"
            target="_blank"
            rel="noopener noreferrer"
            className="group p-6 rounded-2xl bg-[#0A0A0A] border border-white/5 hover:border-[#7dfabf]/30 transition-all duration-500 flex flex-col items-center gap-4"
          >
            <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-[#833AB4] via-[#FD1D1D] to-[#F77737] flex items-center justify-center">
              <Instagram size={28} className="text-white" />
            </div>
            <div>
              <h3 className="text-xl font-bold text-white mb-1 font-heading flex items-center justify-center gap-2">
                Instagram
                <ArrowUpRight size={18} className="text-white/40 group-hover:text-[#7dfabf] group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-all duration-300" />
              </h3>
              <p className="text-white/50 text-sm font-body">@marcusjonescoaching</p>
            </div>
            <p className="text-white/60 text-sm font-body">
              Daily motivation, behind-the-scenes & transformation stories
            </p>
          </a>

          {/* Skool */}
          <a
            href="https://www.skool.com/booze-to-beast"
            target="_blank"
            rel="noopener noreferrer"
            className="group p-6 rounded-2xl bg-[#0A0A0A] border border-white/5 hover:border-[#7dfabf]/30 transition-all duration-500 flex flex-col items-center gap-4"
          >
            <div className="w-14 h-14 rounded-xl bg-[#7dfabf] flex items-center justify-center">
              <Users size={28} className="text-black" />
            </div>
            <div>
              <h3 className="text-xl font-bold text-white mb-1 font-heading flex items-center justify-center gap-2">
                Skool Community
                <ArrowUpRight size={18} className="text-white/40 group-hover:text-[#7dfabf] group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-all duration-300" />
              </h3>
              <p className="text-white/50 text-sm font-body">Free to join</p>
            </div>
            <p className="text-white/60 text-sm font-body">
              Connect with other men on the same journey & get free resources
            </p>
          </a>
        </div>
      </div>
    </section>
  );
};

export const BottomSections = () => {
  return (
    <>
      <EffortSection />
      <TestimonialsSection />
      <FAQSection />
      <InvestmentSection />
      <GuaranteeSection />
      <ConnectSection />
      <ApplySection />
      <Footer />
    </>
  );
};
